import { useState } from 'react';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import SoftInput from 'components/SoftInput';

import { interpolateTemplate } from 'lib/i18n/dictionary';
import {
  addGallerySectionRequest,
  deleteGallerySectionRequest,
  renameGallerySectionRequest,
  type Gallery,
} from 'gallery';

import { GalleryGrid } from './gallery-grid';
import type { GalleryAction } from './gallery-page';
import { GallerySelectionPanel } from './gallery-selection-panel';
import { ConfirmDialog, FieldLabel, sectionLabel, useGalleryText } from './gallery-ui';
import { GalleryUploadZone } from './gallery-upload-zone';

interface GalleryFilesProps {
  gallery: Gallery;
  token?: string;
  run: GalleryAction;
  setGallery: (gallery: Gallery) => void;
  reload: () => Promise<void>;
  scheduleReload: () => void;
  initialView: 'all' | 'selection';
}

export function GalleryFiles({
  gallery,
  token,
  run,
  setGallery,
  reload,
  scheduleReload,
  initialView,
}: GalleryFilesProps) {
  const { t, plural } = useGalleryText();
  const [view, setView] = useState<'all' | 'selection'>(initialView);
  const [sectionUuid, setSectionUuid] = useState<string | null>(null);
  const [newSectionName, setNewSectionName] = useState('');
  const [renameValue, setRenameValue] = useState<string | null>(null);
  const [isDeleteSectionOpen, setIsDeleteSectionOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // A picked section that was deleted meanwhile falls back to all files.
  const current = gallery.sections.find((section) => section.uuid === sectionUuid) ?? null;
  const hasSections = gallery.sections.length > 1;

  const addSection = async () => {
    const name = newSectionName.trim();
    if (!name || busy) {
      return;
    }
    setBusy(true);
    let added: string | null = null;
    const ok = await run(async () => {
      added = (await addGallerySectionRequest(gallery.uuid, name, token)).uuid;
    });
    setBusy(false);
    if (ok) {
      setNewSectionName('');
      setSectionUuid(added);
    }
  };

  const renameSection = async () => {
    const name = renameValue?.trim();
    if (!current || !name) {
      return;
    }
    setBusy(true);
    const ok = await run(async () => {
      await renameGallerySectionRequest(current.uuid, name, token);
    });
    setBusy(false);
    if (ok) {
      setRenameValue(null);
    }
  };

  const deleteSection = async () => {
    if (!current) {
      return;
    }
    setBusy(true);
    const ok = await run(async () => {
      await deleteGallerySectionRequest(current.uuid, token);
    });
    setBusy(false);
    setIsDeleteSectionOpen(false);
    if (ok) {
      setSectionUuid(null);
    }
  };

  return (
    <Card sx={{ p: 2 }} data-testid="gallery-files">
      <SoftBox display="flex" justifyContent="flex-end" mb={1}>
        <Tabs
          value={view}
          onChange={(_event, next: 'all' | 'selection') => setView(next)}
          sx={{ minWidth: 300, '& .MuiTab-root': { px: 2, minWidth: 130, fontSize: '0.875rem' } }}
        >
          <Tab value="all" label={t.files.allFiles} data-testid="gallery-tab-all" />
          <Tab
            value="selection"
            label={interpolateTemplate(t.files.selectionTab, {
              count: String(gallery.selection.count),
            })}
            data-testid="gallery-tab-selection"
          />
        </Tabs>
      </SoftBox>
      {view === 'all' ? (
        <SoftBox display="flex" flexDirection="column" gap={2}>
          <SoftBox
            display="flex"
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            data-testid="gallery-sections"
          >
            {hasSections
              ? gallery.sections.map((section) => (
                  <Chip
                    key={section.uuid}
                    label={`${sectionLabel(section, t.files.sectionUnnamed)} · ${
                      section.items.length
                    }`}
                    color={current?.uuid === section.uuid ? 'primary' : 'default'}
                    onClick={() =>
                      setSectionUuid(current?.uuid === section.uuid ? null : section.uuid)
                    }
                    data-testid="gallery-section-chip"
                  />
                ))
              : null}
            <SoftBox
              component="form"
              display="flex"
              gap={1}
              onSubmit={(event: React.FormEvent) => {
                event.preventDefault();
                void addSection();
              }}
            >
              <SoftInput
                size="small"
                placeholder={t.files.addSectionPlaceholder}
                value={newSectionName}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setNewSectionName(event.target.value)
                }
                inputProps={{
                  'aria-label': t.files.addSectionPlaceholder,
                  'data-testid': 'gallery-add-section-name',
                }}
                sx={{ width: 160 }}
              />
              <SoftButton
                type="submit"
                size="small"
                variant="outlined"
                color="info"
                disabled={busy || !newSectionName.trim()}
                data-testid="gallery-add-section"
              >
                {t.files.addSection}
              </SoftButton>
            </SoftBox>
            {current ? (
              <>
                <SoftButton
                  size="small"
                  variant="text"
                  color="info"
                  onClick={() => setRenameValue(current.name)}
                  data-testid="gallery-rename-section"
                >
                  {t.files.renameSection}
                </SoftButton>
                <SoftButton
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={() => setIsDeleteSectionOpen(true)}
                  disabled={busy}
                  data-testid="gallery-delete-section"
                >
                  {t.files.deleteSection}
                </SoftButton>
              </>
            ) : null}
          </SoftBox>
          <GalleryUploadZone
            galleryUuid={gallery.uuid}
            sectionUuid={current?.uuid ?? null}
            token={token}
            onUploaded={scheduleReload}
            onFinished={reload}
          />
          <GalleryGrid
            gallery={gallery}
            section={current}
            token={token}
            run={run}
            setGallery={setGallery}
          />
        </SoftBox>
      ) : (
        <GallerySelectionPanel gallery={gallery} token={token} run={run} />
      )}
      <Dialog
        open={renameValue !== null}
        onClose={() => setRenameValue(null)}
        fullWidth
        maxWidth="xs"
      >
        <SoftBox
          component="form"
          onSubmit={(event: React.FormEvent) => {
            event.preventDefault();
            void renameSection();
          }}
        >
          <DialogTitle>{t.files.renameSectionTitle}</DialogTitle>
          <DialogContent>
            <FieldLabel htmlFor="gallery-section-name">{t.files.sectionNameLabel}</FieldLabel>
            <SoftInput
              id="gallery-section-name"
              value={renameValue ?? ''}
              autoFocus
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setRenameValue(event.target.value)
              }
              inputProps={{ 'data-testid': 'gallery-section-name' }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <SoftButton variant="outlined" color="secondary" onClick={() => setRenameValue(null)}>
              {t.cancel}
            </SoftButton>
            <SoftButton
              type="submit"
              variant="gradient"
              color="info"
              disabled={busy || !renameValue?.trim()}
              data-testid="gallery-section-name-save"
            >
              {t.files.save}
            </SoftButton>
          </DialogActions>
        </SoftBox>
      </Dialog>
      <ConfirmDialog
        open={isDeleteSectionOpen && Boolean(current)}
        title={interpolateTemplate(t.files.deleteSectionTitle, {
          name: current ? sectionLabel(current, t.files.sectionUnnamed) : '',
        })}
        body={plural(t.files.deleteSectionBody, current?.items.length ?? 0)}
        confirmLabel={t.files.deleteSection}
        cancelLabel={t.cancel}
        busy={busy}
        testId="gallery-delete-section-dialog"
        onCancel={() => setIsDeleteSectionOpen(false)}
        onConfirm={deleteSection}
      />
    </Card>
  );
}
